import { webServerHostUrl } from '../../utilities/url_util';
import { itineraryMetaTagString } from '../../utilities/meta_tags_util';
import { encode } from '../../utilities/member_id_decode_util';
import { getItineraryDescription } from '../../apis/metaData';
import { getLanguageByRank } from '../../utilities/language';

const getSecretId = () => process.env.SECRET_ID_FOR_META_TAGS;

function loadData(urlPath, language, query, params, headers) {
  const tripId = query.id || params.tripId;

  /** URL_FORMAT
   * @type {boolean} IS_NEW 新版網址 (用 params.tripId 是否帶值判斷)
   * @type {boolean} IS_OLD 舊版網址 (用 query.id 是否帶值判斷)
   */
  const URL_FORMAT = {
    IS_NEW: !!params.tripId,
    IS_OLD: !!query.id,
  };

  if (!tripId) {
    return {
      metaData: null,
    };
  }
  const checkoutStatus = (status, results) => {
    const REDIRECT_OBJ = {
      DEFAULT: {
        metaData: null,
      },
      NOT_FOUND: {
        notFound: true,
      },
    };
    /** getRedirect HTTP 狀態說明
     * @type {method} [200] 比對新舊網址並做對應動作 見 getRedirect 內說明
     * @type {method}} [204] 轉回 NOT FOUND 頁
     * @type {method}} [607] 此行程未公開 若 POST ID 與 使用者 ID 不一致就導到首頁
     */
    const getRedirect = {
      '200': () => {
        const {
          title,
          images,
          member,
          pois,
          noindex,
          cities,
          member: { nickname },
          countries,
          dayCount,
          language: authorLanguage,
        } = results;
        const metaLanguage = getLanguageByRank(
          query.hl,
          authorLanguage,
          headers['accept-language'],
        );
        const htmlLang = authorLanguage || 'en';
        const encodeMemberId = encode(member._id, true);

        /** USER_EQUAL
         * @type {boolean} IS_MODIFIED 判斷使用者是否修改過ID
         * @type {boolean} IS_EQUAL_ENCODE_ID 判斷當前網址上ID是否等於舊版ID(Hash ID)
         * @type {boolean} IS_EQUAL_REAL_ID 判斷使用者ID是否與顯示在網址上ID相同
         */
        const USER_EQUAL = {
          IS_MODIFIED: member.userid_modified,
          IS_EQUAL_ENCODE_ID: params.userid === encodeMemberId,
          IS_EQUAL_REAL_ID: params.userid === member.userid,
        };
        const TO_NEW_TRIP_URL = {
          redirect: {
            status: 301,
            url: `${webServerHostUrl()}${member.userid}/trips/${tripId}`,
          },
        };

        /**
         * 200 轉址流程：
         * 當 IS_OLD 成立 將使用者從舊網址轉到新版網址
         * 當 IS_NEW 成立 且 IS_MODIFIED 與 IS_EQUAL_ENCODE_ID 都成立 將使用者轉到新版網址
         * 當 IS_NEW 與 IS_MODIFIED 成立、IS_EQUAL_REAL_ID 卻對不起來時 將使用者轉回首頁
         * 當 IS_MODIFIED 成立、IS_EQUAL_ENCODE_ID 卻為 flase
         * - 即便使用者改過 ID 還是要成功留在新版網址
         *
         * 否則都不是 就轉到渲染添加 metaTag
         */

        if (URL_FORMAT.IS_OLD) {
          return TO_NEW_TRIP_URL;
        } else if (
          // 新網址格式：User 尚未改過新 ID 且當前網址為舊 User ID
          URL_FORMAT.IS_NEW &&
          !USER_EQUAL.IS_MODIFIED &&
          USER_EQUAL.IS_EQUAL_ENCODE_ID
        ) {
          const metaData = itineraryMetaTagString({
            urlPath,
            title,
            images,
            language: metaLanguage,
            pois,
            noindex,
            cities,
            countries,
            dayCount,
            nickname,
          });

          return {
            metaData,
            htmlLang,
          };
        } else if (
          // 新網址格式：User 未改過新 ID 但當前網址 ID 與舊 User ID 不相等
          URL_FORMAT.IS_NEW &&
          !USER_EQUAL.IS_MODIFIED &&
          !USER_EQUAL.IS_EQUAL_ENCODE_ID
        ) {
          return REDIRECT_OBJ.NOT_FOUND;
        } else if (
          // 新網址格式：User 改過新 ID 且當前網址 ID 與 User 舊 ID 相等
          URL_FORMAT.IS_NEW &&
          USER_EQUAL.IS_MODIFIED &&
          USER_EQUAL.IS_EQUAL_ENCODE_ID
        ) {
          return TO_NEW_TRIP_URL;
        } else if (
          // 新網址格式：User 改過新 ID 且當前網址 ID 與 User 新 ID 相等
          URL_FORMAT.IS_NEW &&
          USER_EQUAL.IS_MODIFIED &&
          USER_EQUAL.IS_EQUAL_REAL_ID
        ) {
          const metaData = itineraryMetaTagString({
            urlPath,
            title,
            images,
            language: metaLanguage,
            pois,
            noindex,
            cities,
            countries,
            nickname,
            dayCount,
          });

          return {
            metaData,
            htmlLang,
          };
        }
        return {
          metaData: null,
        };
      },
      '204': () => {
        return REDIRECT_OBJ.NOT_FOUND;
      },
      '400': () => {
        return REDIRECT_OBJ.NOT_FOUND;
      },
      '607': () => {
        if (results && results.member) {
          const { member } = results;

          const TO_NEW_TRIP_URL = {
            redirect: {
              status: 301,
              url: `${webServerHostUrl()}${member.userid}/trips/${tripId}`,
            },
          };

          if (URL_FORMAT.IS_OLD) {
            return TO_NEW_TRIP_URL;
          }
          return REDIRECT_OBJ.DEFAULT;
        }
        return REDIRECT_OBJ.DEFAULT;
      },
    };
    if (!getRedirect[status]) return REDIRECT_OBJ.DEFAULT;
    return getRedirect[status]();
  };

  const metaDataResult = () => {
    const data = {
      secretId: getSecretId(),
      parseTripObjectId: tripId,
      language,
    };
    if (URL_FORMAT.IS_NEW) {
      data.userid = params.userid;
    }
    return getItineraryDescription(data);
  };

  return metaDataResult()
    .then(response => {
      const { status, results } = response.data;
      // console.log('TCL: loadData -> status', status);
      return checkoutStatus(status, results);
    })
    .catch(error => {
      console.log('error', error);
      return {
        metaData: null,
      };
    });
}

export default loadData;
